"use client";

import {
  useEditor,
  useEditorState,
  EditorContent,
  type Editor,
} from "@tiptap/react";
import { textblockTypeInputRule } from "@tiptap/core";
import StarterKit from "@tiptap/starter-kit";
import Heading from "@tiptap/extension-heading";
import Placeholder from "@tiptap/extension-placeholder";
import { Markdown } from "@tiptap/markdown";
import { useEffect, useRef } from "react";
import { Icon, type IconName } from "@/components/ui/icon";

const FixedHeading = Heading.extend({
  addInputRules() {
    return (this.options.levels as number[]).map((level) =>
      textblockTypeInputRule({
        find: new RegExp(`^(#{${level}})\\s$`),
        type: this.type,
        getAttributes: { level },
      }),
    );
  },
});

// StarterKit v3 already bundles Underline; adding it again registers it twice.
const EDITOR_EXTENSIONS = [
  StarterKit.configure({ heading: false }),
  FixedHeading.configure({ levels: [1, 2, 3] }),
  Markdown,
];

interface RichTextEditorProps {
  /** Markdown source. */
  content: string;
  /** Receives markdown on every change. */
  onChange: (markdown: string) => void;
  placeholder?: string;
  wrapperClassName?: string;
  maxHeight?: string;
}

function ToolbarButton({
  onClick,
  isActive,
  icon,
  label,
}: {
  onClick: () => void;
  isActive: boolean;
  icon: string;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`group flex items-center justify-center size-7 rounded-md transition-all cursor-pointer ${
        isActive ? "bg-contrast/[0.10]" : "hover:bg-contrast/6"
      }`}
      aria-label={label}
    >
      <div
        className={`transition-opacity ${isActive ? "opacity-100" : "opacity-50 group-hover:opacity-70"}`}
      >
        <Icon
          name={icon as IconName}
          join="round"
          fill="outlined"
          radius="1"
          stroke="1.5"
          size={16}
          className="text-heading"
        />
      </div>
    </button>
  );
}

function Toolbar({ editor }: { editor: Editor | null }) {
  const active = useEditorState({
    editor,
    selector: (ctx) => ({
      bold: ctx.editor?.isActive("bold") ?? false,
      italic: ctx.editor?.isActive("italic") ?? false,
      underline: ctx.editor?.isActive("underline") ?? false,
      h1: ctx.editor?.isActive("heading", { level: 1 }) ?? false,
      h2: ctx.editor?.isActive("heading", { level: 2 }) ?? false,
      h3: ctx.editor?.isActive("heading", { level: 3 }) ?? false,
      bulletList: ctx.editor?.isActive("bulletList") ?? false,
      orderedList: ctx.editor?.isActive("orderedList") ?? false,
    }),
  });

  if (!editor || !active) return null;
  return (
    <div className="flex items-center gap-0.5 px-2 py-1.5 border-b border-border">
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleBold().run()}
        isActive={active.bold}
        icon="IconBold"
        label="Bold"
      />
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleItalic().run()}
        isActive={active.italic}
        icon="IconItalic"
        label="Italic"
      />
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleUnderline().run()}
        isActive={active.underline}
        icon="IconUnderline"
        label="Underline"
      />
      <div className="w-px h-4 bg-contrast/10 mx-1" />
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
        isActive={active.h1}
        icon="IconH1"
        label="Heading 1"
      />
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
        isActive={active.h2}
        icon="IconH2"
        label="Heading 2"
      />
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
        isActive={active.h3}
        icon="IconH3"
        label="Heading 3"
      />
      <div className="w-px h-4 bg-contrast/10 mx-1" />
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        isActive={active.bulletList}
        icon="IconBulletList"
        label="Bullet List"
      />
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        isActive={active.orderedList}
        icon="IconNumberedList"
        label="Ordered List"
      />
    </div>
  );
}

const EDITOR_VERSION = 3;

/** Empty markdown string parses to a doc with zero blocks, so Placeholder has nothing to decorate. */
function isMarkdownEffectivelyEmpty(markdown: string): boolean {
  return markdown.trim().length === 0;
}

function editorPayloadFromMarkdown(markdown: string): {
  content: string;
  contentType: "html" | "markdown";
} {
  if (isMarkdownEffectivelyEmpty(markdown)) {
    return { content: "<p></p>", contentType: "html" };
  }
  return { content: markdown, contentType: "markdown" };
}

export function RichTextEditor({
  content,
  onChange,
  placeholder,
  wrapperClassName,
  maxHeight,
}: RichTextEditorProps) {
  const editorClass = [
    "prose prose-invert max-w-none px-3 py-2 outline-none text-sm text-contrast/90 cursor-text",
    "[&_.is-editor-empty::before]:text-contrast/30 [&_.is-editor-empty::before]:content-[attr(data-placeholder)] [&_.is-editor-empty::before]:float-left [&_.is-editor-empty::before]:h-0 [&_.is-editor-empty::before]:pointer-events-none",
    "[&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:pl-0.5",
    "[&_h1]:text-xl [&_h1]:font-bold [&_h1]:mb-2 [&_h1]:mt-1 [&_h2]:text-lg [&_h2]:font-bold [&_h2]:mb-1.5 [&_h2]:mt-1 [&_h3]:text-base [&_h3]:font-semibold [&_h3]:mb-1 [&_h3]:mt-0.5",
    "[&_p]:text-sm [&_p]:leading-normal",
    maxHeight ? "" : "min-h-[120px]",
  ].join(" ");

  const lastSyncedContent = useRef(content);

  const editor = useEditor(
    {
      extensions: [
        ...EDITOR_EXTENSIONS,
        Placeholder.configure({
          placeholder: placeholder || "Start writing...",
          showOnlyCurrent: false,
        }),
      ],
      immediatelyRender: false,
      ...editorPayloadFromMarkdown(content),
      onUpdate: ({ editor: e }) => {
        const markdown = e.getMarkdown();
        lastSyncedContent.current = markdown;
        onChange(markdown);
      },
      editorProps: {
        attributes: {
          class: editorClass,
          ...(maxHeight ? { style: `min-height: ${maxHeight}` } : {}),
        },
      },
    },
    [EDITOR_VERSION, maxHeight],
  );

  useEffect(() => {
    if (!editor || content === lastSyncedContent.current) return;
    const payload = editorPayloadFromMarkdown(content);
    editor.commands.setContent(payload.content, {
      emitUpdate: false,
      contentType: payload.contentType,
    });
    lastSyncedContent.current = content;
  }, [content, editor]);

  if (!editor) return null;

  return (
    <div
      className={
        wrapperClassName ??
        "rounded-lg border border-contrast/15 bg-contrast/6 overflow-hidden shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)]"
      }
    >
      <Toolbar editor={editor} />
      <div
        className="overflow-y-auto"
        style={maxHeight ? { height: maxHeight } : undefined}
        onMouseDown={(e) => {
          if (e.button !== 0) return;
          const el = e.target as HTMLElement;
          if (el.closest(".tiptap")) return;
          editor.chain().focus().run();
        }}
      >
        <EditorContent
          editor={editor}
          className={maxHeight ? "h-full min-h-0" : "min-h-[120px]"}
        />
      </div>
    </div>
  );
}
